import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from "@mui/material";
import type { EmployeeAgreementFileResult } from "@services/riskAnalysis";
import { formatOcrUserError } from "@utils/extractionProgress";

/** Aligns with Discover / Potential Penalties — slate headings, teal thead, neutral chrome */
const EA_SLATE = "#2c4e60";
const EA_MUTED = "#8c98a5";
const EA_TEAL_HEAD = "#3e829a";
const EA_LINK = "#3e829a";
const EA_BORDER = "#d1d5db";
const EA_ROW_ALT = "#f9fafb";

const STATUS_TAB_INDEX = 5;

const eaTableHeadCellSx = {
  fontWeight: 700,
  fontFamily: "Rethink Sans, sans-serif",
  color: "#fff",
  bgcolor: EA_TEAL_HEAD,
  textTransform: "uppercase" as const,
  letterSpacing: "0.04em",
  fontSize: "12px",
  borderColor: EA_BORDER,
};

function EaSectionTitle(props: {
  title: string;
  first?: boolean;
}): React.ReactElement {
  const { title, first } = props;
  const idx = title.indexOf(" — ");
  return (
    <Typography
      component="h4"
      sx={{
        mt: first ? 0 : 2.5,
        mb: 0,
        px: 1.5,
        py: 1.25,
        fontSize: "14px",
        fontWeight: 700,
        color: EA_SLATE,
        lineHeight: 1.35,
        border: `1px solid ${EA_BORDER}`,
        borderBottom: "none",
        borderRadius: "4px 4px 0 0",
        fontFamily: "Rethink Sans, sans-serif",
        boxSizing: "border-box",
      }}
    >
      {idx === -1 ? (
        title
      ) : (
        <>
          {title.slice(0, idx)}
          <Box component="span" sx={{ fontWeight: 400, color: EA_MUTED }}>
            {title.slice(idx)}
          </Box>
        </>
      )}
    </Typography>
  );
}

function TabPanel(props: {
  children?: React.ReactNode;
  index: number;
  value: number;
}): React.ReactElement {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`employee-agreement-tabpanel-${index}`}
      aria-labelledby={`employee-agreement-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

function EmployeeAgreementUploadProgress(props: {
  files: File[];
  loading: boolean;
  fileResults?: EmployeeAgreementFileResult[] | null;
  hideTitle?: boolean;
  embeddedInTab?: boolean;
}): React.ReactElement | null {
  const {
    files,
    loading,
    fileResults,
    hideTitle,
    embeddedInTab,
  } = props;
  const [textDialog, setTextDialog] = useState<{
    filename: string;
    text: string;
  } | null>(null);

  const hasResults =
    fileResults != null &&
    Array.isArray(fileResults) &&
    fileResults.length > 0;

  const emptyData = !files.length && !hasResults;
  if (emptyData) {
    if (embeddedInTab) {
      return (
        <Typography
          sx={{
            color: EA_MUTED,
            py: 1,
            fontFamily: "Rethink Sans, sans-serif",
            fontSize: "14px",
          }}
        >
          No file processing details are available for this analysis.
        </Typography>
      );
    }
    return null;
  }
  if (!loading && !hasResults && !embeddedInTab) {
    return null;
  }

  type ProgressRow = {
    filename: string;
    statusLabel: string;
    error?: string | null;
    extractedText?: string | null;
    pdfCaption?: string | null;
    pageCaption?: string | null;
  };
  const rows: ProgressRow[] = [];
  if (files.length) {
    for (const f of files) {
      const r = fileResults?.find((x) => x.filename === f.name);
      let statusLabel: string;
      let err: string | null | undefined;
      let extractedText: string | null | undefined;
      let pdfCaption: string | null = null;
      let pageCaption: string | null = null;
      if (r?.status === "extracting") {
        statusLabel = "Extracting…";
        if (r.pdf_may_ocr) {
          pdfCaption = "PDF — text extraction may run OCR (slow)";
        }
        if (
          r.ocr_page != null &&
          r.ocr_total_pages != null &&
          Number.isFinite(r.ocr_page) &&
          Number.isFinite(r.ocr_total_pages)
        ) {
          pageCaption = `Page ${r.ocr_page} / ${r.ocr_total_pages}`;
        }
      } else if (r?.status === "queued") {
        statusLabel = "Queued";
        if (r.pdf_may_ocr) {
          pdfCaption = "PDF — text extraction may run OCR (slow)";
        }
      } else if (r) {
        if (r.status === "processed") statusLabel = "Processed";
        else if (r.status === "empty")
          statusLabel = "Not processed (no usable text)";
        else statusLabel = "Not processed";
        err = formatOcrUserError(r.error);
        extractedText =
          r.extracted_text != null ? r.extracted_text : undefined;
      } else if (loading && !r) {
        statusLabel = "Processing…";
      } else {
        statusLabel = "—";
      }
      rows.push({
        filename: f.name,
        statusLabel,
        error: err,
        extractedText,
        pdfCaption,
        pageCaption,
      });
    }
  } else if (hasResults && fileResults) {
    for (const r of fileResults) {
      let statusLabel: string;
      if (r.status === "processed") statusLabel = "Processed";
      else if (r.status === "empty")
        statusLabel = "Not processed (no usable text)";
      else if (r.status === "queued") statusLabel = "Queued";
      else if (r.status === "extracting") statusLabel = "Extracting…";
      else statusLabel = "Not processed";
      rows.push({
        filename: r.filename,
        statusLabel,
        error: formatOcrUserError(r.error),
        extractedText: r.extracted_text != null ? r.extracted_text : undefined,
        pdfCaption: null,
        pageCaption: null,
      });
    }
  }

  const canShowExtracted = (row: ProgressRow) =>
    typeof row.extractedText === "string" && row.extractedText.length > 0;

  return (
    <Box
      sx={{
        mt: hideTitle ? 0 : 1.5,
        p: hideTitle ? 0 : 1.5,
        borderRadius: hideTitle ? 0 : "4px",
        border: hideTitle ? "none" : `1px solid ${EA_BORDER}`,
        backgroundColor: hideTitle ? "transparent" : "#f8fdfe",
        textAlign: "left",
        width: "100%",
      }}
    >
      {!hideTitle ? (
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: 700,
            color: EA_SLATE,
            mb: 1,
            fontFamily: "Rethink Sans, sans-serif",
          }}
        >
          File processing status
        </Typography>
      ) : null}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: `1px solid ${EA_BORDER}`,
          borderRadius: "4px",
          maxHeight: 360,
          backgroundColor: "#fff",
        }}
      >
        <Table size="small" stickyHeader sx={{ tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 700,
                  fontFamily: "Rethink Sans, sans-serif",
                  width: "36%",
                  backgroundColor: "#e8f7fb",
                  color: EA_SLATE,
                }}
              >
                File name
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 700,
                  fontFamily: "Rethink Sans, sans-serif",
                  width: "18%",
                  backgroundColor: "#e8f7fb",
                  color: EA_SLATE,
                }}
              >
                Status
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 700,
                  fontFamily: "Rethink Sans, sans-serif",
                  width: "30%",
                  backgroundColor: "#e8f7fb",
                  color: EA_SLATE,
                }}
              >
                Error
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 700,
                  fontFamily: "Rethink Sans, sans-serif",
                  width: "16%",
                  backgroundColor: "#e8f7fb",
                  color: EA_SLATE,
                }}
                align="center"
              >
                Extracted text
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow key={`${i}-${row.filename}`}>
                <TableCell
                  sx={{
                    fontSize: "13px",
                    verticalAlign: "top",
                    wordBreak: "break-word",
                    fontFamily: "Rethink Sans, sans-serif",
                  }}
                >
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {row.filename}
                  </Box>
                  {row.pdfCaption ? (
                    <Typography
                      component="div"
                      variant="caption"
                      sx={{ color: EA_MUTED, display: "block", mt: 0.25 }}
                    >
                      {row.pdfCaption}
                    </Typography>
                  ) : null}
                  {row.pageCaption ? (
                    <Typography
                      component="div"
                      variant="caption"
                      sx={{ color: EA_MUTED, display: "block", mt: 0.25 }}
                    >
                      {row.pageCaption}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: "13px",
                    color: "#5c5c5c",
                    verticalAlign: "top",
                    fontFamily: "Rethink Sans, sans-serif",
                  }}
                >
                  {row.statusLabel}
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: "12px",
                    color: row.error ? "#c62828" : "#9e9e9e",
                    verticalAlign: "top",
                    wordBreak: "break-word",
                    fontFamily: "Rethink Sans, sans-serif",
                  }}
                >
                  {row.error || "—"}
                </TableCell>
                <TableCell align="center" sx={{ verticalAlign: "top" }}>
                  <Button
                    type="button"
                    size="small"
                    variant="outlined"
                    disabled={!canShowExtracted(row)}
                    onClick={() =>
                      setTextDialog({
                        filename: row.filename,
                        text: row.extractedText || "",
                      })
                    }
                    sx={{
                      textTransform: "none",
                      fontFamily: "Rethink Sans, sans-serif",
                      fontSize: "12px",
                    }}
                  >
                    View OCR text
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={textDialog != null}
        onClose={() => setTextDialog(null)}
        fullWidth
        maxWidth="md"
        aria-labelledby="ea-extracted-dialog-title"
      >
        <DialogTitle id="ea-extracted-dialog-title">
          Extracted text{textDialog ? ` — ${textDialog.filename}` : ""}
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ pt: 2, fontFamily: "ui-monospace, monospace", fontSize: "13px" }}
        >
          <Box
            component="pre"
            sx={{
              margin: 0,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              maxHeight: "60vh",
              overflow: "auto",
            }}
          >
            {textDialog?.text ?? ""}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={() => setTextDialog(null)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function EmployeeAgreementAnalysisPlaceholder(): React.ReactElement {
  return (
    <Typography
      sx={{
        color: EA_MUTED,
        py: 2,
        fontFamily: "Rethink Sans, sans-serif",
        fontSize: "14px",
      }}
    >
      Analysis results will appear here after processing completes successfully.
    </Typography>
  );
}

function formatKeyLabel(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export type EmployeeAgreementResultsProps = {
  /** LLM analysis; null while processing or if the run failed before analysis. */
  analysis: Record<string, unknown> | null;
  /** True while extraction + LLM are in progress; all tabs non-interactive, Status shown. */
  loading?: boolean;
  /** Original upload list (used to build Status rows while loading or after success). */
  statusFiles?: File[];
  /** Per-file extraction outcomes from API metadata or error payload. */
  statusFileResults?: EmployeeAgreementFileResult[] | null;
  /** Shown after a successful run so the user can return to the upload drop zone. */
  onRequestNewAnalysis?: () => void;
};

/** Render physician employment v2 `analysis` with tabs including file processing Status. */
export function EmployeeAgreementResults(
  props: EmployeeAgreementResultsProps,
): React.ReactElement {
  const {
    analysis,
    loading = false,
    statusFiles = [],
    statusFileResults = null,
    onRequestNewAnalysis,
  } = props;

  const [tab, setTab] = useState(0);

  useEffect(() => {
    if (loading) {
      setTab(STATUS_TAB_INDEX);
      return;
    }
    if (analysis) {
      setTab(0);
    } else {
      setTab(STATUS_TAB_INDEX);
    }
  }, [loading, analysis]);

  const analysisObj = analysis ?? {};
  const hasAnalysis = Boolean(analysis);
  const meta = (analysisObj.metadata as Record<string, unknown>) || {};
  const sectionA =
    (analysisObj.section_a_executive_summary as Record<string, unknown>) || {};
  const topFindings = (sectionA.top_findings as Record<string, unknown>[]) || [];
  const sectionD =
    (analysisObj.section_d_quick_summary as Record<string, unknown>) || {};
  const sectionB =
    (analysisObj.section_b_compliance_review as Record<
      string,
      unknown
    >[]) || [];
  const sectionC =
    (analysisObj.section_c_penalty_exposure as Record<string, unknown>[]) ||
    [];
  const sectionE =
    (analysisObj.section_e_amendment_reconciliation as Record<
      string,
      unknown
    >[]) || [];
  const sectionF =
    (analysisObj.section_f_missing_evidence as Record<string, unknown>[]) ||
    [];

  const analysisTabDisabled = loading || !hasAnalysis;
  /** Status stays usable during a run so per-file OCR text can be opened as each file finishes. */
  const statusTabDisabled = false;

  return (
    <Box
      sx={{
        width: "100%",
        mt: 1,
        "& .MuiTableCell-root": { borderColor: EA_BORDER },
        "& a": {
          color: EA_LINK,
          fontWeight: 500,
          textDecoration: "none",
          "&:hover": { textDecoration: "underline" },
        },
      }}
    >
      {onRequestNewAnalysis &&
      !loading &&
      (hasAnalysis ||
        (Array.isArray(statusFileResults) && statusFileResults.length > 0)) ? (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
          <Button
            type="button"
            size="small"
            variant="text"
            onClick={onRequestNewAnalysis}
            sx={{
              textTransform: "none",
              fontFamily: "Rethink Sans, sans-serif",
              fontSize: "13px",
              color: EA_SLATE,
            }}
          >
            New analysis
          </Button>
        </Box>
      ) : null}

      <Tabs
        value={tab}
        onChange={(_, v) => {
          if (loading && v !== STATUS_TAB_INDEX) return;
          setTab(v);
        }}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          borderBottom: 1,
          borderColor: "#e5e7eb",
          "& .MuiTab-root": {
            fontFamily: "Rethink Sans, sans-serif",
            fontSize: "13px",
            fontWeight: 600,
            textTransform: "none",
            color: EA_MUTED,
            minHeight: 44,
          },
          "& .MuiTab-root.Mui-selected": {
            color: EA_SLATE,
          },
          "& .MuiTabs-indicator": {
            backgroundColor: EA_SLATE,
            height: 3,
          },
        }}
      >
        <Tab
          label="Summary"
          id="employee-agreement-tab-0"
          disabled={analysisTabDisabled}
        />
        <Tab
          label="Compliance review"
          id="employee-agreement-tab-1"
          disabled={analysisTabDisabled}
        />
        <Tab
          label="Penalty exposure"
          id="employee-agreement-tab-2"
          disabled={analysisTabDisabled}
        />
        <Tab
          label="Amendments"
          id="employee-agreement-tab-3"
          disabled={analysisTabDisabled}
        />
        <Tab
          label="Missing evidence"
          id="employee-agreement-tab-4"
          disabled={analysisTabDisabled}
        />
        <Tab label="Status" id="employee-agreement-tab-5" disabled={statusTabDisabled} />
      </Tabs>

      <TabPanel value={tab} index={0}>
        {!hasAnalysis ? (
          <EmployeeAgreementAnalysisPlaceholder />
        ) : (
          <>
        <EaSectionTitle title="Metadata" first />
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            mb: 2,
            border: `1px solid ${EA_BORDER}`,
            borderTop: "none",
            borderRadius: "0 0 4px 4px",
            overflow: "hidden",
          }}
        >
          <Table size="small">
            <TableBody>
              {[
                ["Schema version", String(meta.schema_version ?? "")],
                ["Analysis timestamp", String(meta.analysis_timestamp ?? "")],
                [
                  "Documents analyzed",
                  Array.isArray(meta.documents_analyzed)
                    ? (meta.documents_analyzed as string[]).join("; ")
                    : "",
                ],
                ["Analyst note", String(meta.analyst_note ?? "")],
              ].map(([k, v]) => (
                <TableRow key={k} sx={{ "&:nth-of-type(odd)": { bgcolor: EA_ROW_ALT } }}>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      width: "28%",
                      fontFamily: "Rethink Sans, sans-serif",
                      color: EA_SLATE,
                    }}
                  >
                    {k}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "pre-wrap", color: "#262626" }}>
                    {v || "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <EaSectionTitle title="Executive summary — top findings" />
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            mb: 2,
            border: `1px solid ${EA_BORDER}`,
            borderTop: "none",
            borderRadius: "0 0 4px 4px",
            overflow: "hidden",
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                {["Rank", "Flag", "Category", "Finding", "Implication", "Reference"].map(
                  (h) => (
                    <TableCell key={h} sx={eaTableHeadCellSx}>
                      {h}
                    </TableCell>
                  ),
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {topFindings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>No findings</TableCell>
                </TableRow>
              ) : (
                topFindings.map((row, i) => (
                  <TableRow
                    key={i}
                    sx={{ "&:nth-of-type(even)": { bgcolor: EA_ROW_ALT } }}
                  >
                    <TableCell>{String(row.rank ?? "")}</TableCell>
                    <TableCell>{String(row.flag_level ?? "")}</TableCell>
                    <TableCell>{String(row.category ?? "")}</TableCell>
                    <TableCell sx={{ maxWidth: 220, whiteSpace: "pre-wrap" }}>
                      {String(row.finding ?? "")}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220, whiteSpace: "pre-wrap" }}>
                      {String(row.implication ?? "")}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 180, whiteSpace: "pre-wrap" }}>
                      {String(row.document_reference ?? "")}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <EaSectionTitle title="Quick summary" />
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: `1px solid ${EA_BORDER}`,
            borderTop: "none",
            borderRadius: "0 0 4px 4px",
            overflow: "hidden",
          }}
        >
          <Table size="small">
            <TableBody>
              {Object.entries(sectionD).map(([key, val]) => (
                <TableRow
                  key={key}
                  sx={{ "&:nth-of-type(odd)": { bgcolor: EA_ROW_ALT } }}
                >
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      width: "32%",
                      fontFamily: "Rethink Sans, sans-serif",
                      color: EA_SLATE,
                      verticalAlign: "top",
                    }}
                  >
                    {formatKeyLabel(key)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "pre-wrap", color: "#262626" }}>
                    {Array.isArray(val) ? (val as string[]).join(", ") : String(val ?? "")}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
          </>
        )}
      </TabPanel>

      <TabPanel value={tab} index={1}>
        {!hasAnalysis ? (
          <EmployeeAgreementAnalysisPlaceholder />
        ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: `1px solid ${EA_BORDER}`,
            borderRadius: "4px",
            overflow: "hidden",
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                {[
                  "Topic",
                  "Category",
                  "What documents say",
                  "Flag",
                  "Why it matters",
                  "Proven violation",
                  "Validation needed",
                  "URLs",
                ].map((h) => (
                  <TableCell key={h} sx={eaTableHeadCellSx}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sectionB.map((row, i) => (
                <TableRow key={i} sx={{ "&:nth-of-type(even)": { bgcolor: EA_ROW_ALT } }}>
                  <TableCell sx={{ whiteSpace: "pre-wrap" }}>
                    {String(row.topic ?? "")}
                  </TableCell>
                  <TableCell>{String(row.category ?? "")}</TableCell>
                  <TableCell sx={{ maxWidth: 200, whiteSpace: "pre-wrap" }}>
                    {String(row.what_documents_say ?? "")}
                  </TableCell>
                  <TableCell>{String(row.flag_level ?? "")}</TableCell>
                  <TableCell sx={{ maxWidth: 200, whiteSpace: "pre-wrap" }}>
                    {String(row.why_it_matters ?? "")}
                  </TableCell>
                  <TableCell>{String(row.proven_violation ?? "")}</TableCell>
                  <TableCell sx={{ maxWidth: 180, whiteSpace: "pre-wrap" }}>
                    {String(row.validation_needed ?? "")}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 160, whiteSpace: "pre-wrap", fontSize: "12px" }}>
                    {Array.isArray(row.regulation_urls)
                      ? (row.regulation_urls as string[]).join("\n")
                      : ""}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        )}
      </TabPanel>

      <TabPanel value={tab} index={2}>
        {!hasAnalysis ? (
          <EmployeeAgreementAnalysisPlaceholder />
        ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: `1px solid ${EA_BORDER}`,
            borderRadius: "4px",
            overflow: "hidden",
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                {[
                  "Exposure",
                  "What contract shows",
                  "Flag",
                  "Assessment",
                  "Proven violation",
                  "External evidence",
                  "URLs",
                ].map((h) => (
                  <TableCell key={h} sx={eaTableHeadCellSx}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sectionC.map((row, i) => (
                <TableRow key={i} sx={{ "&:nth-of-type(even)": { bgcolor: EA_ROW_ALT } }}>
                  <TableCell sx={{ whiteSpace: "pre-wrap" }}>
                    {String(row.exposure_item ?? "")}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200, whiteSpace: "pre-wrap" }}>
                    {String(row.what_contract_shows ?? "")}
                  </TableCell>
                  <TableCell>{String(row.flag_level ?? "")}</TableCell>
                  <TableCell sx={{ maxWidth: 200, whiteSpace: "pre-wrap" }}>
                    {String(row.exposure_assessment ?? "")}
                  </TableCell>
                  <TableCell>{String(row.proven_violation ?? "")}</TableCell>
                  <TableCell sx={{ maxWidth: 180, whiteSpace: "pre-wrap" }}>
                    {String(row.external_evidence_needed ?? "")}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 160, whiteSpace: "pre-wrap", fontSize: "12px" }}>
                    {Array.isArray(row.regulation_urls)
                      ? (row.regulation_urls as string[]).join("\n")
                      : ""}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        )}
      </TabPanel>

      <TabPanel value={tab} index={3}>
        {!hasAnalysis ? (
          <EmployeeAgreementAnalysisPlaceholder />
        ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: `1px solid ${EA_BORDER}`,
            borderRadius: "4px",
            overflow: "hidden",
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                {[
                  "Document",
                  "Effective",
                  "Execution",
                  "Key changes",
                  "Supersession",
                  "Consistent",
                  "Comment",
                ].map((h) => (
                  <TableCell key={h} sx={eaTableHeadCellSx}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sectionE.map((row, i) => (
                <TableRow key={i} sx={{ "&:nth-of-type(even)": { bgcolor: EA_ROW_ALT } }}>
                  <TableCell sx={{ whiteSpace: "pre-wrap" }}>
                    {String(row.document_name ?? "")}
                  </TableCell>
                  <TableCell>{String(row.effective_date ?? "")}</TableCell>
                  <TableCell>{String(row.execution_status ?? "")}</TableCell>
                  <TableCell sx={{ maxWidth: 220, whiteSpace: "pre-wrap" }}>
                    {Array.isArray(row.key_changes)
                      ? (row.key_changes as string[]).join("; ")
                      : ""}
                  </TableCell>
                  <TableCell>{String(row.supersession_language ?? "")}</TableCell>
                  <TableCell>{String(row.internally_consistent ?? "")}</TableCell>
                  <TableCell sx={{ maxWidth: 200, whiteSpace: "pre-wrap" }}>
                    {String(row.reconciliation_comment ?? "")}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        )}
      </TabPanel>

      <TabPanel value={tab} index={4}>
        {!hasAnalysis ? (
          <EmployeeAgreementAnalysisPlaceholder />
        ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: `1px solid ${EA_BORDER}`,
            borderRadius: "4px",
            overflow: "hidden",
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                {["Item", "Priority", "Why needed", "Absence risk"].map((h) => (
                  <TableCell key={h} sx={eaTableHeadCellSx}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sectionF.map((row, i) => (
                <TableRow key={i} sx={{ "&:nth-of-type(even)": { bgcolor: EA_ROW_ALT } }}>
                  <TableCell sx={{ whiteSpace: "pre-wrap" }}>
                    {String(row.item ?? "")}
                  </TableCell>
                  <TableCell>{String(row.priority ?? "")}</TableCell>
                  <TableCell sx={{ maxWidth: 260, whiteSpace: "pre-wrap" }}>
                    {String(row.why_needed ?? "")}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 220, whiteSpace: "pre-wrap" }}>
                    {String(row.absence_risk ?? "")}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        )}
      </TabPanel>

      <TabPanel value={tab} index={STATUS_TAB_INDEX}>
        <EmployeeAgreementUploadProgress
          hideTitle
          embeddedInTab
          files={statusFiles}
          loading={loading}
          fileResults={statusFileResults}
        />
      </TabPanel>
    </Box>
  );
}
